/**
 * The colour-theme preference: Light, Dark or System. Per browser, never per account.
 *
 * - The default is Light, which is how every screen looked before this existed. Nobody gets Dark by accident: a device
 *   that prefers dark changes nothing until the person chooses System or Dark.
 * - Stored in localStorage under THEME_STORAGE_KEY. Every read and write is wrapped: a private window, blocked site
 *   data or a full quota must never take the page down, so a failed read is Light and a failed write only means the
 *   choice lasts until the tab closes.
 * - Applied on <html> as data-theme="light|dark" plus the `dark` class, which is exactly what the tokens already key on
 *   (index.css and styles/tide/tide.css select `[data-tenant="emerge"][data-theme="dark"]` / `.dark`; Tailwind's
 *   darkMode is "class"). public/index.html carries the same few lines inline so the attribute is on <html> before the
 *   first paint; applyTheme here is idempotent and runs again once React mounts.
 * - System follows the device live: the prefers-color-scheme listener re-resolves while the preference is System.
 *   Another tab's choice arrives through the storage event.
 *
 * - Light-only routes: the admin console hard-codes dark ink on a light ground in dozens of files, so under /admin the
 *   theme RESOLVES to light whatever the preference is (the preference itself is untouched, and the switch is not shown
 *   there). ThemeRouteGuard (inside the router) reports the route; leaving /admin re-applies the preference.
 *
 * A plain module store (no React context) so the switch works wherever a shell mounts it, with or without the provider.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'emerge.theme';
export const DEFAULT_THEME: ThemePreference = 'light';
export const DARK_QUERY = '(prefers-color-scheme: dark)';
export const THEME_CHOICES: readonly ThemePreference[] = ['light', 'dark', 'system'];

export interface ThemeState {
  preference: ThemePreference;
  resolved: ResolvedTheme;
}

export const isThemePreference = (v: unknown): v is ThemePreference => v === 'light' || v === 'dark' || v === 'system';

/** The saved choice, or Light when there is none, it is not one of the three, or storage throws. */
export function readPreference(): ThemePreference {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(v) ? v : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

/** A choice whose write failed (blocked or full storage). It wins over whatever storage still says, for this tab only. */
let memoryPreference: ThemePreference | null = null;

function writePreference(p: ThemePreference): boolean {
  try { window.localStorage.setItem(THEME_STORAGE_KEY, p); return true; } catch { return false; /* the choice lasts for this tab only */ }
}

/** What to apply now: the in-memory choice when storage could not keep it, else the stored one. */
function currentPreference(): ThemePreference {
  return memoryPreference ?? readPreference();
}

function systemMedia(): MediaQueryList | null {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;
  } catch {
    return null;
  }
}

/** Routes whose screens are light-only for now (the admin console). */
export const LIGHT_ONLY_PREFIXES: readonly string[] = ['/admin'];
export const isLightOnlyPath = (pathname: string): boolean =>
  LIGHT_ONLY_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));

/** Set by ThemeRouteGuard; null until then, when the address bar answers (a hard load at /admin). */
let lightOnlyRoute: boolean | null = null;
function onLightOnlyRoute(): boolean {
  if (lightOnlyRoute !== null) return lightOnlyRoute;
  return typeof window !== 'undefined' && isLightOnlyPath(window.location.pathname);
}

export function resolveTheme(p: ThemePreference): ResolvedTheme {
  if (onLightOnlyRoute()) return 'light';
  if (p === 'dark') return 'dark';
  if (p === 'system') return systemMedia()?.matches ? 'dark' : 'light';
  return 'light';
}

/** Put the resolved theme on <html>. */
export function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.classList.toggle('dark', resolved === 'dark');
}

let state: ThemeState | null = null;
const listeners = new Set<() => void>();

function compute(p: ThemePreference): ThemeState {
  return { preference: p, resolved: resolveTheme(p) };
}

/** Replace the snapshot only when it changed, so useSyncExternalStore sees a stable value. */
function commit(next: ThemeState): void {
  const same = state && state.preference === next.preference && state.resolved === next.resolved;
  state = same ? state : next;
  applyTheme(state!.resolved);
  if (!same) listeners.forEach((l) => l());
}

export function getThemeState(): ThemeState {
  if (!state) state = compute(currentPreference());
  return state;
}

export function setThemePreference(p: ThemePreference): void {
  if (!isThemePreference(p)) return;
  memoryPreference = writePreference(p) ? null : p;
  commit(compute(p));
}

/** The router reports whether the current route is light-only; the page follows at once, and the preference returns on leaving. */
export function setLightOnlyRoute(on: boolean): void {
  if (lightOnlyRoute === on) return;
  lightOnlyRoute = on;
  commit(compute(getThemeState().preference));
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/* ── Live sources: the device's scheme and other tabs ───────────────── */

let holders = 0;
let detach: (() => void) | null = null;

function attach(): () => void {
  const mq = systemMedia();
  const onScheme = () => { const cur = getThemeState(); if (cur.preference === 'system') commit(compute('system')); };
  const onStorage = (e: StorageEvent) => {
    if (e.key !== null && e.key !== THEME_STORAGE_KEY) return;
    memoryPreference = null; // another tab chose: that choice is the newer one
    commit(compute(readPreference()));
  };
  if (mq) {
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onScheme);
    else if (typeof (mq as MediaQueryList).addListener === 'function') mq.addListener(onScheme);
  }
  window.addEventListener('storage', onStorage);
  return () => {
    if (mq) {
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', onScheme);
      else if (typeof (mq as MediaQueryList).removeListener === 'function') mq.removeListener(onScheme);
    }
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * Start following the device and other tabs, and apply the stored choice now. Reference counted: the provider and every
 * mounted switch hold one; the listeners go when the last releases.
 */
export function retainTheme(): () => void {
  holders += 1;
  if (holders === 1) detach = attach();
  commit(compute(currentPreference()));
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders -= 1;
    if (holders === 0 && detach) { detach(); detach = null; }
  };
}

/** Tests only: forget the cached snapshot so the next read goes to storage again. */
export function __resetThemeStore(): void {
  if (detach) { detach(); detach = null; }
  holders = 0;
  state = null;
  memoryPreference = null;
  lightOnlyRoute = null;
  listeners.clear();
}
